import React, { useCallback, useEffect, useRef, useState } from 'react';

import router from 'next/router';
import useStoreApp from '@/store';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useRouter as useRouterNext13 } from 'next13-progressbar';
import useTourBooking from '@/hooks/useTourBooking';
import { useTourBookingContext } from '@/containers/TourBookingByCustomer/providers/useTourBookingContext';
import { PaymentMethodEnum } from '@/containers/TourBookingByCustomer/providers/TourBookingProvider';

import { tourBookingService } from '@/services/tourBookingService';
import { paymentService } from '@/services/paymentService';
import type { OnePayPaymentMethod } from '@/services/paymentService';
import { bookingService } from '@/services/bookingService';

import moment, { duration } from 'moment';
import { Box, CircularProgress, Grid, Modal, Typography } from '@mui/material';
import BookingInfoSection from './BookingInfoSection';
import PaymentSummarySection from './PaymentSummarySection';
import { ICreateTourBooking, ITrip } from '@/types/interface';
import { BookingType, PaymentStatus, PaymentType, SocketType, SourceType } from '@/types/consistency';
import { formatDateDisplay, formatDateRequest, formatTimeDisplay, pushToBookingDetail } from '@/utils/utils';

import {
  BookingConfirmContainer,
  BookingInfoBox,
  PaymentInfoBox,
  BookingTitle,
  PaymentTitle,
} from './style';
import { handleLocalStorage } from '@/utils/handleLocalStorage';
import { hoiangoShowLog } from '@/utils/log';
import PayPalPaymentModal from '@/components/PayPalPaymentModal';
import WaitingPayment from '../WaitingPayment';
import { trackTourBookingStep } from '@/utils/gtag';
import Toast from '@/components/Toast';

import type { PendingOrder } from '@/types/pendingOrder';
import { readPendingOrder, removePaidPendingOrder } from '@/utils/pendingOrder';
import { storeOnePayCallbackOrder } from '@/utils/onePayWindow';
export type { PendingOrder } from '@/types/pendingOrder';

interface BookingConfirmProps {
  pendingOrder?: PendingOrder;
  sx?: any
}

const ONEPAY_LINK_TIMEOUT_MS = (29 * 60 + 30) * 1000;
const ONEPAY_PAYMENT_METHODS = [
  PaymentMethodEnum.INTCARD,
  PaymentMethodEnum.APPLEPAY,
  PaymentMethodEnum.SAMSUNGPAY,
  PaymentMethodEnum.GOOGLEPAY,
  PaymentMethodEnum.VNPAYQR,
  PaymentMethodEnum.VIETQRPAYGLOBAL,
  PaymentMethodEnum.VTBBANKQR,
];

const isOnePayPaymentMethod = (paymentMethod: string) =>
  ONEPAY_PAYMENT_METHODS.includes(paymentMethod as PaymentMethodEnum);

/*
const paymentMethods = [
  {
    key: 'PAYPAL',
    label: 'Paypal',
    icon: '/assets/icons/paypal.svg',
    disabled: false,
  },
  {
    key: 'VNPAY',
    label: 'VN Pay',
    icon: '/assets/icons/vnpay.svg',
    disabled: false,
  },
  {
    key: 'PAY_LATER',
    label: 'common:payLater',
    icon: '',
    disabled: false,
  },
];
*/

interface PaymentMethod {
  key: PaymentMethodEnum;
  label: string;
  icons: string[];
  iconGroups?: string[][];
  iconHeight?: number;
  paymentType: string;
  onePayPaymentMethod?: OnePayPaymentMethod;
  disabled: boolean;
}

const paymentMethods: PaymentMethod[] = [
  {
    key: PaymentMethodEnum.PAYPAL,
    label: 'common:payment_methods.paypal',
    icons: ['/assets/icons/payment/ic_paypal.svg'],
    paymentType: PaymentType.PAYPAL.code,
    disabled: false,
  },
  {
    key: PaymentMethodEnum.INTCARD,
    label: 'common:payment_methods.credit_debit_card',
    icons: [
      '/assets/icons/payment/onepay-images/VS.svg',
      '/assets/icons/payment/onepay-images/MC.svg',
      '/assets/icons/payment/onepay-images/amex.svg',
      '/assets/icons/payment/onepay-images/JC.svg',
    ],
    paymentType: PaymentType.OnePay.code,
    onePayPaymentMethod: 'INTERCARD',
    disabled: false,
  },
  {
    key: PaymentMethodEnum.APPLEPAY,
    label: 'common:payment_methods.apple_pay',
    icons: [
      '/assets/icons/payment/onepay-images/applepay.svg',
      '/assets/icons/payment/onepay-images/VS.svg',
      '/assets/icons/payment/onepay-images/MC.svg',
    ],
    iconGroups: [
      ['/assets/icons/payment/onepay-images/applepay.svg'],
      [
        '/assets/icons/payment/onepay-images/VS.svg',
        '/assets/icons/payment/onepay-images/MC.svg',
      ],
    ],
    paymentType: PaymentType.OnePay.code,
    onePayPaymentMethod: 'APPLEPAY',
    disabled: false,
  },
  {
    key: PaymentMethodEnum.SAMSUNGPAY,
    label: 'common:payment_methods.samsung_pay',
    icons: ['/assets/icons/payment/onepay-images/samsungpay.svg'],
    iconHeight: 24,
    paymentType: PaymentType.OnePay.code,
    onePayPaymentMethod: 'SAMSUNGPAY',
    disabled: false,
  },
  {
    key: PaymentMethodEnum.GOOGLEPAY,
    label: 'common:payment_methods.google_pay',
    icons: ['/assets/icons/payment/onepay-images/googlepay.svg'],
    paymentType: PaymentType.OnePay.code,
    onePayPaymentMethod: 'GOOGLEPAY',
    disabled: false,
  },
  {
    key: PaymentMethodEnum.VNPAYQR,
    label: 'common:payment_methods.vietqr',
    icons: ['/assets/icons/payment/onepay-images/vietqr.svg'],
    paymentType: PaymentType.OnePay.code,
    onePayPaymentMethod: 'VIETQR',
    disabled: false,
  },
  {
    key: PaymentMethodEnum.VIETQRPAYGLOBAL,
    label: 'common:payment_methods.qr_global',
    icons: ['/assets/icons/payment/onepay-images/vietqrpay_global.svg'],
    paymentType: PaymentType.OnePay.code,
    onePayPaymentMethod: 'VIETQRPAYGLOBAL',
    disabled: false,
  },
  {
    key: PaymentMethodEnum.VTBBANKQR,
    label: 'common:payment_methods.qr_vtc_russia',
    icons: ['/assets/icons/payment/onepay-images/qr-vrb.svg'],
    paymentType: PaymentType.OnePay.code,
    onePayPaymentMethod: 'VTBBANKQR',
    disabled: false,
  },
  {
    key: PaymentMethodEnum.PAY_LATER,
    label: 'common:payLater',
    icons: ['/assets/images/ic_cash.png'],
    paymentType: PaymentType.Cash.code,
    disabled: false,
  }
];

declare global {
  interface Window {
    paypal: any;
  }
}

const BookingConfirm = React.forwardRef<HTMLElement, BookingConfirmProps & { paymentInfoBoxRef?: React.Ref<HTMLElement> }>(
  ({ pendingOrder, sx, paymentInfoBoxRef }, ref) => {
    const { t } = useTranslation();
    const pathName = usePathname();
    const routerNext13 = useRouterNext13();
    const { collaboratorId } = router.query;

    const {
      bookingTourData,
      services,
      tourDetail,
      contactInfo,
      computedPricing,
      storePromotion,
      setBookingTourData,
      setContactInfo,
      setComputedPricing,
      showConfirmModal,
      finishRenderPaypalButtons,
      setFinishRenderPaypalButtons,
      durationOptions,
    } = useTourBookingContext();

    const {
      doSubmit,
      successBooking,
      bookingDone,
      loading,
      bookingErrMsg,
      createPaypalPayment,
      onCapturePaypalPayment,
      showPaypalPaymentModal,
      setShowPaypalPaymentModal,
      paypalOrderId,
      setPaypalOrderId,
      paypalPaymentApproved,
      setPaypalPaymentApproved,
      setBookingErrMsg,
      embedWaitingPayment,
      setEmbedWaitingPayment,
      doUpdatePaymentType,
    } =
      useTourBooking({
        service: tourBookingService,
        tourType: undefined,
        currentTour: null
      });

    const [selectedPayment, setSelectedPayment] = useState<string>(PaymentMethodEnum.PAY_LATER);
    const { languageId, inPayment, setInPayment, socketBookingMessage } = useStoreApp(state => state.globalSlice);

    const [showMBBankPaymentModal, setShowMBBankPaymentModal] = useState<boolean>(false);
    const [isMBBankProcessing, setIsMBBankProcessing] = useState<boolean>(false);
    const [mbBankPaymentURL, setMBBankPaymentURL] = useState<string>("");

    const onePayRequestInFlight = useRef(false);
    const createTourInFlight = useRef(false);
    const [isOnePayProcessing, setIsOnePayProcessing] = useState<boolean>(false);
    const onePayLinkExpirationTimer = useRef<number>();
    const onePayLinkExpiresAt = useRef<number>();
    const onePayLinkPaymentMethod = useRef<string>();
    const [showToastError, setShowToastError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const [forceClosePaypalPaymentModal, setForceClosePaypalPaymentModal] = useState<boolean>(false);
    const [orderConfirmed, setOrderConfirmed] = useState<PendingOrder | undefined>(undefined);

    const paypalRef = useRef<HTMLDivElement>(null);

    const clearOnePayLinkExpiration = useCallback(() => {
      if (onePayLinkExpirationTimer.current !== undefined) {
        window.clearTimeout(onePayLinkExpirationTimer.current);
        onePayLinkExpirationTimer.current = undefined;
      }
      onePayLinkExpiresAt.current = undefined;
      onePayLinkPaymentMethod.current = undefined;
    }, []);

    const invalidateOnePayLink = useCallback(() => {
      clearOnePayLinkExpiration();
    }, [clearOnePayLinkExpiration]);

    const scheduleOnePayLinkExpiration = useCallback((paymentMethod: string) => {
      clearOnePayLinkExpiration();
      const expiresAt = Date.now() + ONEPAY_LINK_TIMEOUT_MS;
      onePayLinkExpiresAt.current = expiresAt;
      onePayLinkPaymentMethod.current = paymentMethod;
      onePayLinkExpirationTimer.current = window.setTimeout(() => {
        if (onePayLinkExpiresAt.current === expiresAt) {
          invalidateOnePayLink();
        }
      }, ONEPAY_LINK_TIMEOUT_MS);
    }, [clearOnePayLinkExpiration, invalidateOnePayLink]);

    const isOnePayLinkValid = useCallback((paymentMethod: string) => {
      const expiresAt = onePayLinkExpiresAt.current;
      if (
        !expiresAt ||
        onePayLinkPaymentMethod.current !== paymentMethod ||
        Date.now() >= expiresAt
      ) {
        invalidateOnePayLink();
        return false;
      }
      return true;
    }, [invalidateOnePayLink]);

    const handlePaymentMethodChange = useCallback((paymentMethod: string) => {
      if (paymentMethod !== selectedPayment) {
        invalidateOnePayLink();
      }
      setSelectedPayment(paymentMethod);
    }, [invalidateOnePayLink, selectedPayment]);

    function storeOrderConfirm(orderConfirm: PendingOrder) {
      setOrderConfirmed(orderConfirm);
      handleLocalStorage.set('order_confirm', JSON.stringify(orderConfirm));
    }

    const createAndStoreConfirmOrder = () => {

      // Store order info in localStorage before redirect
      if (tourDetail && bookingDone.codeTracking) {

        let haveBankCode = selectedPayment != PaymentMethodEnum.PAYPAL && selectedPayment != PaymentMethodEnum.PAY_LATER;

        let orderConfirmed: PendingOrder = {
          order_id: bookingDone.id,
          tour_id: tourDetail.id,
          slug: tourDetail.slug,
          order_codeTracking: bookingDone.codeTracking,
          payment_status: 'waiting',
          //duration: bookingTourData.duration,
          tourPriceOptionId: bookingTourData.tourPriceOptionId,
          tourPriceOptions: tourDetail.tourPriceOptions,
          paymentType: bookingDone.paymentType,
          bankCode: haveBankCode ? selectedPayment : undefined,
          code: bookingDone.code,
        };
        storeOrderConfirm(orderConfirmed);

        return orderConfirmed;
      }

      return undefined;
    }

    function onMBBankPayment() {
      if (bookingDone.id) {
        setIsMBBankProcessing(true);

        const res = paymentService.getMBBankUrl(bookingDone.id);

        res.then((rs: any) => {
          if (rs.data) {
            setMBBankPaymentURL(rs.data.paymentUrl);
            setShowMBBankPaymentModal(true);
          }
        })
      }
    };

    const renderPaypalButtons = () => {
      try {
        if (typeof window !== 'undefined' && window.paypal) {
          return window.paypal.Buttons({
            createOrder: async function (data: any, actions: any) {
              const orderId = await createPaypalPayment();
              setPaypalOrderId(orderId);
              return orderId;
            },
            onApprove: async function (data: any, actions: any) {
              const res = await onCapturePaypalPayment();
              if (res) {
                setShowPaypalPaymentModal(false);
                setPaypalPaymentApproved(true);
                setTimeout(() => {
                  pushToBookingDetail(router, bookingDone.codeTracking);
                }, 1000);
              }
            },
          })
            .render(paypalRef?.current) // '#paypal-button'
            .then(() => {
              if (!finishRenderPaypalButtons && setFinishRenderPaypalButtons) {
                setFinishRenderPaypalButtons(true);
              }
            });
        }
        else {
          return Promise.reject(new Error('PayPal SDK is not available'));
        }
      }
      catch (error) {
        console.group('renderPaypalButtons -> error');
        console.log(error);
        console.groupEnd();
        return Promise.reject(error);
      }
    };

    const openOnePayWindow = (order: PendingOrder, paymentUrl: string, paymentMethod: string) => {
      if (!isOnePayLinkValid(paymentMethod)) return false;

      try {
        storeOnePayCallbackOrder(order);
      } catch {
        setBookingErrMsg(t('onepayCallback.linkError') as string);
        return false;
      }

      if (!isOnePayLinkValid(paymentMethod)) return false;

      const left = window.screenX + (window.innerWidth - 600) / 2;
      const top = window.screenY + (window.innerHeight - 800) / 2;
      const popup = window.open(
        paymentUrl,
        'OnePayPayment',
        `width=600,height=800,left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`,
      );
      if (!popup) {
        // Try a regular tab as a fallback for browsers that block named popups.
        const tab = window.open(paymentUrl, '_blank');
        if (!tab) {
          setBookingErrMsg(t('onepayCallback.popupBlocked') as string);
          return false;
        }
      }

      setBookingErrMsg('');
      return true;
    };

    const onOnePayPayment = async () => {
      if (!bookingDone?.id || onePayRequestInFlight.current) return;

      onePayRequestInFlight.current = true;
      setIsOnePayProcessing(true);
      invalidateOnePayLink();
      setBookingErrMsg('');
      try {
        const order = createAndStoreConfirmOrder();
        const stored = readPendingOrder();
        if (!order?.code || !order.order_codeTracking ||
          stored?.order_id !== order.order_id || stored?.code !== order.code ||
          stored?.order_codeTracking !== order.order_codeTracking ||
          stored?.paymentType !== PaymentType.OnePay.code) {
          throw new Error('Pending order unavailable');
        }
        const selectedMethod = paymentMethods.find(method => method.key === selectedPayment);
        const paymentMethod = selectedMethod?.onePayPaymentMethod;
        if (!paymentMethod) {
          throw new Error('OnePay payment method unavailable');
        }
        const { data } = await paymentService.getOnePayUrl(
          bookingDone.id,
          { paymentMethods: [paymentMethod] },
        );
        if (typeof data?.paymentUrl !== 'string' || !data.paymentUrl) {
          throw new Error('Payment URL unavailable');
        }
        order.onepayUrl = data.paymentUrl;
        storeOrderConfirm(order);
        scheduleOnePayLinkExpiration(selectedPayment);
        openOnePayWindow(order, data.paymentUrl, selectedPayment);
      } catch {
        setBookingErrMsg(t('onepayCallback.linkError') as string);
      } finally {
        onePayRequestInFlight.current = false;
        setIsOnePayProcessing(false);
      }
    };

    useEffect(() => {
      const { display } = sx;
      if (display === 'none' && !showConfirmModal) {
        setBookingErrMsg('');
      }
    }, [sx.display])

    useEffect(() => {
      const { display } = sx;

      if (display === 'none') return;

      if (!inPayment) {
        setInPayment(true);
      }

      return () => {
        setInPayment(false);
      };
    }, [sx.display]);

    useEffect(() => {
      return () => {
        clearOnePayLinkExpiration();
      };
    }, [clearOnePayLinkExpiration]);

    useEffect(() => {

      if (!successBooking) return;

      switch (selectedPayment) {
        case PaymentMethodEnum.PAYPAL:
          // TODO: re-check forece close here !!!
          if (forceClosePaypalPaymentModal) return;

          createAndStoreConfirmOrder();
          setShowPaypalPaymentModal(true);
          break;

        case PaymentMethodEnum.INTCARD:
        case PaymentMethodEnum.APPLEPAY:
        case PaymentMethodEnum.SAMSUNGPAY:
        case PaymentMethodEnum.GOOGLEPAY:
        case PaymentMethodEnum.VNPAYQR:
        case PaymentMethodEnum.VIETQRPAYGLOBAL:
        case PaymentMethodEnum.VTBBANKQR:
          void onOnePayPayment();
          break;

        case PaymentMethodEnum.PAY_LATER:
          if (bookingDone.codeTracking) {
            pushToBookingDetail(router, bookingDone.codeTracking);
          }
          break;

        case PaymentMethodEnum.MBBANK:
          onMBBankPayment();
          break;

        default:
          // Default logic
          break;
      }
    }, [successBooking, pushToBookingDetail, forceClosePaypalPaymentModal]);

    useEffect(() => {
      if (!socketBookingMessage || !bookingDone) return;

      const order = socketBookingMessage.data as ITrip;

      if (
        socketBookingMessage.type === SocketType.UpdatePaymentOnePaySuccess &&
        order.id === bookingDone.id &&
        order.paymentStatus === PaymentStatus.Paid.code
      ) {
        invalidateOnePayLink();
        pushToBookingDetail(router, bookingDone.codeTracking);
        return;
      }

      if (
        socketBookingMessage.type === SocketType.UpdatePaymentOnePayFail &&
        order.id === bookingDone.id
      ) {
        invalidateOnePayLink();
        setErrorMessage('onepayCallback.transactionFailed');
        setShowToastError(true);
      }
    }, [bookingDone, invalidateOnePayLink, socketBookingMessage]);

    // Effect: If pendingOrder, check payment status
    useEffect(() => {

      const checkPendingOrder = async () => {
        if (pendingOrder && pendingOrder.order_codeTracking) {
          try {
            const { data }: any = await bookingService.getDetailBooking(pendingOrder.order_codeTracking);

            if ((data.paymentType === PaymentType.VNPAY.code ||
              data.paymentType === PaymentType.PAYPAL.code ||
              data.paymentType === PaymentType.OnePay.code) &&
              data.paymentStatus === PaymentStatus.Paid.code) {
              removePaidPendingOrder(data);
            } else {
              // Load order detail data into fields
              if (data) {
                // Map data fields to bookingTourData and passenger as needed
                setComputedPricing((prev) => ({
                  ...prev,
                  price: data.price ?? 0,
                  discount: data.discount ?? 0,
                  discount_percent: data.discount_percent ?? 0,
                  discount_price: data.discount ?? 0,
                  visitPlaceTicketFee: data.visitPlaceTicketFee ?? 0,
                  additional_price: data.additionalPrice ?? 0,
                  money: data.money ?? 0,
                  pureTourPrice: data.pureTourPrice ?? 0,
                  additional_service_list: data.additional_service_list ?? [],
                  visit_place_list: data.visit_place_list ?? [],
                  additional_passenger_price: data.additional_passenger_price ?? 0,
                }));

                setBookingTourData((prev) => ({
                  ...prev,
                  tourId: data.tourId,
                  tourPriceOptionId: data.tourPriceOptionId,
                  date: data.scheduleDate ? moment(data.scheduleDate) : prev.date,
                  time: data.scheduleDate ? moment(data.scheduleDate) : prev.time,
                  additionalServiceIds: data.additionalServiceIds || [],
                  pickUpAddress: data.pickUpAddress || '',
                  pickUp: data.pickUp || undefined,
                  passengerNormal: data.passengerNormal || 0,
                  passengerHalf: data.passengerHalf || 0,
                  note: data.note || '',
                  promotionDetailId: data.promotionDetailId,
                  promotionId: data.promotionId,
                  collaboratorId: data.collaboratorId,
                }));

                setContactInfo({
                  fullName: data.passenger?.fullName || '',
                  phone: data.passenger?.phone || '',
                  email: data.passenger?.email || '',
                  zalo: data.passenger?.zalo || undefined,
                  whatsapp: data.passenger?.whatsapp || undefined,
                  kakaotalk: data.passenger?.kakaotalk || undefined,
                  viber: data.passenger?.viber || undefined,
                  dialCode: data.passenger?.dialCode || undefined,
                });

              }
            }
          } catch (e) {
            // handle error
          }
        }
      };

      if (pendingOrder) {
        const storeOrder = pendingOrder;

        if (storeOrder.bankCode && paymentMethods.some(method => method.key === storeOrder.bankCode)) {
          handlePaymentMethodChange(storeOrder.bankCode);
        }

        checkPendingOrder();
      }
    }, [pendingOrder, setBookingTourData, setContactInfo]);

    const handleUpdatePaymentNewOrder = useCallback(() => {

      const order = pendingOrder || orderConfirmed;

      if (!order) return;

      const selectedMethod = paymentMethods.find(method => method.key === selectedPayment);
      if (!selectedMethod) return;

      const currentPaymentType = bookingDone?.paymentType ?? order.paymentType;
      if (selectedMethod.paymentType !== currentPaymentType) {
        void doUpdatePaymentType(order.order_id, selectedMethod.paymentType);
      } else if (isOnePayPaymentMethod(selectedPayment)) {
        void onOnePayPayment();
      }
      else if (selectedPayment === PaymentMethodEnum.PAYPAL) {
        setShowPaypalPaymentModal(true);
        setForceClosePaypalPaymentModal(false);
      }
    }, [bookingDone, pendingOrder, orderConfirmed, doUpdatePaymentType, onOnePayPayment, selectedPayment]);

    const onSubmit = useCallback(() => {
      if (pendingOrder || orderConfirmed) {
        handleUpdatePaymentNewOrder();
        return;
      }

      if (selectedPayment === PaymentType.PAYPAL.code && successBooking && !showPaypalPaymentModal && forceClosePaypalPaymentModal) {
        setShowPaypalPaymentModal(true);
        setForceClosePaypalPaymentModal(false);
        return;
      }
      else if (bookingDone) {
        if (isOnePayPaymentMethod(selectedPayment)) {
          void onOnePayPayment();
          return;
        }
        setForceClosePaypalPaymentModal(false);
        return;
      }

      const parseTime = bookingTourData.time.format(formatTimeDisplay);
      const parseDate = bookingTourData.date.format(formatDateRequest);
      const selectedDateTimeGMT7 = moment(`${parseDate} ${parseTime}:00 GMT+0700`, 'YYYY-MM-DD HH:mm:ss GMT+0700');
      const scheduleDate = selectedDateTimeGMT7.toDate();
      // const selectedDateTimeGMT7 = moment(`${parseDate} ${parseTime}`, 'YYYY-MM-DD HH:mm');
      // const selectedDateTimeZ = selectedDateTimeGMT7.format('YYYY-MM-DD HH:mm:ss Z');
      //
      // const scheduleDate = new Date(`${selectedDateTimeZ}`);

      let paymentType = PaymentType.Cash.code

      const isOnePay = isOnePayPaymentMethod(selectedPayment);

      if (isOnePay) {
        paymentType = PaymentType.OnePay.code;
      }

      else if (selectedPayment === PaymentType.MBBANK.code) {
        paymentType = PaymentType.MBBANK.code;
      }

      else if (selectedPayment === PaymentType.PAYPAL.code) {
        paymentType = PaymentType.PAYPAL.code;
      }
      let bookingType: string = BookingType.WebCustomer.code
      let sourceType: string = SourceType.Customer.code

      if (pathName.includes('/mobile')) {
        bookingType = BookingType.AppCustomer.code

        if (collaboratorId) {
          sourceType = SourceType.Partner.code
        }
        else {
          sourceType = SourceType.Customer.code
        }
      }

      let body: ICreateTourBooking = {
        tourId: tourDetail!.id,
        //duration: bookingTourData.duration,
        tourPriceOptionId: bookingTourData.tourPriceOptionId,
        scheduleDate,
        additionalServiceIds: bookingTourData.additionalServiceIds,
        pickUpAddress: bookingTourData.pickUpAddress,
        pickUp: bookingTourData.pickUp,
        passengerNormal: bookingTourData.passengerNormal,
        passengerHalf: bookingTourData.passengerHalf ?? 0,
        note: bookingTourData.note,
        contactInfo,
        languageId,
        collaboratorId: (collaboratorId as string) ?? undefined,
        paymentType: paymentType,
        bookingType: bookingType,
        sourceType: sourceType,
        promotionId: (storePromotion && storePromotion.promotionId) ? storePromotion.promotionId : undefined,
        promotionDetailId: (storePromotion && storePromotion.promotionDetailId) ? storePromotion.promotionDetailId : undefined,
      };

      if (createTourInFlight.current) return;
      createTourInFlight.current = true;
      void doSubmit(body).finally(() => {
        createTourInFlight.current = false;
      });

      trackTourBookingStep(tourDetail!.id, 'confirmation');

    }, [
      bookingTourData.additionalServiceIds,
      bookingTourData.date,
      //bookingTourData.duration,
      bookingTourData.tourPriceOptionId,
      bookingTourData.note,
      bookingTourData.passengerHalf,
      bookingTourData.passengerNormal,
      bookingTourData.pickUp,
      bookingTourData.pickUpAddress,
      bookingTourData.time,
      bookingDone,
      collaboratorId,
      contactInfo,
      doSubmit,
      handleUpdatePaymentNewOrder,
      languageId,
      onOnePayPayment,
      orderConfirmed,
      pendingOrder,
      tourDetail,
      selectedPayment,
      storePromotion,
      forceClosePaypalPaymentModal,
      showPaypalPaymentModal,
      successBooking,
    ]);

    return (
      <>
        <BookingConfirmContainer sx={sx}>
          <Grid container spacing={2}>
            {/* Left: Booking Info */}
            <Grid item xs={12} md={7} order={{ xs: 1, md: 1 }}>
              <BookingInfoBox>
                <BookingTitle>
                  {t('bookingInfo')}
                </BookingTitle>
                <BookingInfoSection />
              </BookingInfoBox>
            </Grid>
            {/* Right: Payment Info */}
            <Grid item xs={12} md={5} order={{ xs: 2, md: 2 }}>
              {/* Attach the ref here */}
              <PaymentInfoBox sx={{ display: embedWaitingPayment ? 'none' : 'block' }}>
                <PaymentTitle>
                  {t('common:paymentInfo')}
                </PaymentTitle>
                {
                  isMBBankProcessing || loading ?
                    (
                      <CircularProgress />
                    ) :
                    (
                      <PaymentSummarySection
                        t={t}
                        selectedPaymentMethodRef={paymentInfoBoxRef}
                        storePromotion={storePromotion}
                        selectedPayment={selectedPayment}
                        setSelectedPayment={handlePaymentMethodChange}
                        paymentMethods={paymentMethods}
                        bookingErrMsg={bookingErrMsg}
                        disabled={isOnePayProcessing || isMBBankProcessing || loading || showPaypalPaymentModal}
                        onConfirm={(confirmPaymentMethod: string) => {
                          if (confirmPaymentMethod !== selectedPayment) {
                            handlePaymentMethodChange(confirmPaymentMethod)
                          }
                          onSubmit();
                        }}
                      />
                    )
                }
              </PaymentInfoBox>

              {
                embedWaitingPayment &&
                tourDetail &&
                bookingDone &&
                <WaitingPayment orderId={bookingDone.id} codeTracking={bookingDone.codeTracking} />
              }
            </Grid>
          </Grid>
        </BookingConfirmContainer>

        <Toast
          open={showToastError && !!errorMessage}
          onClose={() => {
            setShowToastError(false);
            setErrorMessage(undefined);
          }}
          severity="error"
          message={errorMessage ? t(errorMessage) : ''}
        />

        <PayPalPaymentModal
          paypalRef={paypalRef}
          isOpen={showPaypalPaymentModal}
          renderPaypalButtons={renderPaypalButtons}
          onClose={() => {
            setPaypalOrderId(undefined);
            setShowPaypalPaymentModal(false)
            setForceClosePaypalPaymentModal(true)
          }}
          paypalOrderId={paypalOrderId}
          paypalPaymentApproved={paypalPaymentApproved}
        />

      </>
    );
  }
);

BookingConfirm.displayName = 'BookingConfirm';

export default BookingConfirm;
